<template>
  <div :key="formType">
    <DateSegmented
      required
      :autofocus="!isUpdate && !isMobileApp"
      :date-label="$t(`medicalProcedure.${medicalProcedureData.type}.date`)"
      :error-text="$t(`medicalProcedure.${medicalProcedureData.type}.errorDateEmpty`)"
      :error-text-date-range="
        $t(
          `medicalProcedure.${medicalProcedureData.type}.errorDateBounds${
            formType === 'appointment' ? 'Future' : 'Past'
          }`
        )
      "
      :today-date-checkbox="formType !== 'appointment'"
      :today-date-checkbox-label="
        $t(`medicalProcedure.${medicalProcedureData.type}.todayDateCheckboxLabel`)
      "
      :identifier="formType"
      :min-value="
        formType !== 'appointment'
          ? $auth.user.birthDate
          : new Date(Date.now() - 24 * 60 * 60 * 1000).toString()
      "
      :max-value="formType === 'report' ? new Date().toISOString().split('T')[0] : null"
      @blur="onDateBlur"
      @focus="sendGtm($event.target.dataset.part)"
      @todayClick="
        sendGtm($t(`medicalProcedure.${medicalProcedureData.type}.todayDateCheckboxLabel`))
      "
    />
    <TimeSegmented
      v-if="formType === 'appointment'"
      required
      class="mt-4"
      :label="$t(`medicalProcedure.${medicalProcedureData.type}.time`)"
      :error-text="timeErrorText"
      :error-text-required="$t(`medicalProcedure.${medicalProcedureData.type}.errorTimeRequired`)"
      @blur="onTimeBlur"
      @blurPart="validateDateTime"
      @focus="sendGtm($event.target.dataset.part)"
    />
    <label for="notes" class="mt-4">{{ $t('medicalProcedure.notes') }}</label>
    <v-textarea
      id="notes"
      v-model="entryNotes"
      data-cy="medicalProcedureFormContentTextArea"
      flat
      solo
      auto-grow
      :placeholder="$t('medicalProcedure.notesPlaceholder')"
      rows="4"
      hide-details
      @focus="sendGtm('notes')"
    />
    <Attachments
      class="mt-4"
      :label="$t('medicalProcedure.uploadFiles')"
      :identifier="`medicalProcedureForm-${formType}`"
      @click="sendGtm('attachments')"
      @attachment_downloaded="attachmentDownloaded"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import partnerInfo from '~/mixins/partnerInfo';

export default {
  name: 'MedicalProcedureFormContent',
  mixins: [partnerInfo],
  props: {
    isUpdate: {
      type: Boolean,
      default: false
    },
    medicalProcedureData: {
      type: Object,
      required: true
    },
    formType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isDateTimeValid: null
    };
  },
  computed: {
    entryNotes: {
      get() {
        return this.$store.state.profile.prevention.entryNotes;
      },
      set(value) {
        this.$store.commit('profile/prevention/updateEntryNotes', value);
      }
    },
    ...mapGetters({
      date: 'forms/date/date',
      dateIsValid: 'forms/date/isValid',
      time: 'forms/time/time'
    }),
    timeErrorText() {
      if (!this.isDateTimeValid && this.isDateTimeValid !== null) {
        return this.$t(
          `medicalProcedure.${this.medicalProcedureData.type}.errorDateBounds${
            this.formType === 'appointment' ? 'Future' : 'Past'
          }`
        );
      }

      return '';
    }
  },
  watch: {
    date() {
      this.validateDateTime();
    }
  },
  mounted() {
    this.$eventHub.$on(`validateMedicalProcedureDateTime_${this.formType}`, done => {
      this.$eventHub.$emit(`validateDateSegmented_${this.formType}`, () => {
        if (this.formType === 'appointment') {
          this.$eventHub.$emit('validateTimeSegmented', () => {
            this.validateDateTime();
            this.$nextTick(() => done(this.isDateTimeValid));
          });
          return;
        }

        this.$nextTick(() => done(this.dateIsValid));
      });
    });
  },
  beforeDestroy() {
    this.$eventHub.$off(`validateMedicalProcedureDateTime_${this.formType}`);
  },
  methods: {
    onDateBlur() {
      this.$eventHub.$emit('validateDateSegmented', () => {});

      this.$el.querySelector(this.formType === 'appointment' ? '#time-hour' : '#notes').focus();
    },
    onTimeBlur() {
      this.$el.querySelector('#notes').focus();

      this.$nextTick(() => {
        this.$emit('validateTimeSegmented', () => {
          this.validateDateTime();
        });
      });
    },
    validateDateTime() {
      const dateToValidate = new Date(`${this.date}T${this.time}:00`);
      if (isNaN(dateToValidate)) {
        return;
      }

      const todayDate = new Date();
      this.isDateTimeValid =
        this.formType === 'appointment'
          ? +dateToValidate >= +todayDate
          : +dateToValidate <= +todayDate;
    },
    sendGtm(field) {
      this.gtmPush({
        event: 'form_progress',
        'form.field': field,
        'form.name': `${this.isUpdate ? 'editace' : 'zapsani'} ${this.formType}`,
        'medicalProcedure.type': this.medicalProcedureData.type,
        'medicalProcedure.name': this.medicalProcedureData.title
      });
    },
    attachmentDownloaded() {
      this.sendAnalyticsUl({
        activity: 'prevention_procedure_attachment_downloaded',
        partner_id: this.partnerId,
        partner_name: this.partnerName,
        activity_name: this.medicalProcedureData.title,
        activity_id: this.$route.query?.id
      });
      this.sendGtm({
        event: 'button_click',
        'button.type': 'attachment',
        'button.detail': 'editace zaznamu',
        service: 'prevence'
      });
    }
  }
};
</script>
